import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, ParamMap} from "@angular/router";
import {catchError, finalize, last, map, switchMap, tap} from "rxjs/operators";
import {StudentV3RestService} from "../../services/rest/v3/student-rest.service";
import {V3Student} from "../../model/v3/schools";
import {
  V3Address,
  V3ContactEntry,
  V3PersonalSensitiveDetails,
  V3PersonDetails,
  V3Settings
} from "../../model/v3/people";
import {PeopleV3RestService} from "../../services/rest/v3/people-rest.service";
import {V3FileItem} from "../../model/v3/file";
import {concat, Observable, of} from "rxjs";
import {SerializedTaskQueue} from "../../services/utils/serialized-task-queue";

@Component({
    selector: 'app-manager-student-casa-profile-page',
    templateUrl: './manager-student-casa-profile-page.component.html',
    styleUrls: ['./manager-student-casa-profile-page.component.scss'],
    standalone: false
})
export class ManagerStudentCasaProfilePageComponent implements OnInit, OnDestroy {
  private schoolId: number;
  private studentId: number;
  private personId: number;
  // Saving the profile emits several save events at once; the queue runs the
  // resulting requests sequentially so they cannot overwrite each other.
  private saveQueue = new SerializedTaskQueue();
  activeSubscriptions = 0
  personDetails: V3PersonDetails
  sensitiveDetails: V3PersonalSensitiveDetails
  settings: V3Settings
  address: V3Address
  photo: V3FileItem | null
  contacts: V3ContactEntry[];

  constructor(
    activatedRoute: ActivatedRoute,
    private studentV3Rest: StudentV3RestService,
    private peopleV3Rest: PeopleV3RestService,
  ) {
    activatedRoute.parent.paramMap.pipe(
      map<ParamMap, [number, number]>(params => [Number(params.get("schoolId")), Number(params.get("studentId"))]),
      tap<[number, number]>( ([schoolId, studentId]) => this.storeIds(schoolId, studentId)),
      switchMap(_ => this.loadStudent())
    ).subscribe(
    )
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.saveQueue.dispose()
  }

  loadStudent() {
    return this.loadPersonDetails().pipe(
      switchMap(_ => this.loadPhoto()),
      switchMap(_ => this.loadSettings()),
      switchMap(_ => this.loadSensitive()),
      switchMap(_ => this.loadAddress()),
      switchMap(_ => this.loadContacts()),
    )
  }

  loadPersonDetails() {
    return this.studentV3Rest.getStudent(this.schoolId, this.studentId).pipe(
      tap<V3Student>(student => {
        this.personId = student.person.id
        this.personDetails = student.person.details;
      })
    )
  }

  loadPhoto() {
    return this.peopleV3Rest.getPhoto(this.personId).pipe(
      tap(photo => this.photo = photo)
    )
  }

  loadSettings() {
    return this.peopleV3Rest.getSettings(this.personId).pipe(
      tap(settings => this.settings = settings)
    )
  }

  loadSensitive() {
    return this.peopleV3Rest.getSensitive(this.personId).pipe(
      tap(sensitiveDetails => this.sensitiveDetails = sensitiveDetails)
    )
  }

  loadAddress() {
    return this.peopleV3Rest.getAddress(this.personId).pipe(
      tap(address => this.address = address)
    )
  }

  loadContacts() {
    return this.peopleV3Rest.listContacts(this.personId).pipe(
      tap(contactEntries => this.contacts = contactEntries)
    )
  }

  private storeIds(schoolId: number, studentId: number) {
    this.schoolId = schoolId;
    this.studentId = studentId;
  }

  savePersonDetails(value: V3PersonDetails) {
    this.activeSubscriptions++
    this.saveQueue.enqueue(this.peopleV3Rest.updatePerson(this.personId, value).pipe(
      switchMap(_ => this.loadPersonDetails()),
      finalize(() => this.activeSubscriptions--)
    ))
  }

  saveSensitiveDetails(value: V3PersonalSensitiveDetails) {
    this.activeSubscriptions++
    this.saveQueue.enqueue(this.peopleV3Rest.updateSensitive(this.personId, value).pipe(
      switchMap(_ => this.loadSensitive()),
      finalize(() => this.activeSubscriptions--)
    ))
  }

  saveSettings(value: V3Settings) {
    this.activeSubscriptions++
    this.saveQueue.enqueue(this.peopleV3Rest.updateSettings(this.personId, value).pipe(
      switchMap(_ => this.loadSettings()),
      finalize(() => this.activeSubscriptions--)
    ))
  }

  saveAddress(value: V3Address) {
    this.activeSubscriptions++
    this.saveQueue.enqueue(this.peopleV3Rest.updateAddress(this.personId, value).pipe(
      switchMap(_ => this.loadAddress()),
      finalize(() => this.activeSubscriptions--)
    ))
  }

  saveContacts(values: V3ContactEntry[]) {
    this.activeSubscriptions++
    let observables: Observable<unknown>[] = new Array<Observable<unknown>>();
    values.forEach(value => {
      value.id == null
        ? observables.push(this.peopleV3Rest.createContact(this.personId, value))
        : observables.push(this.peopleV3Rest.updateContact(this.personId, value.id, value))
    })
    this.saveQueue.enqueue(concat(
      ...observables
    ).pipe(
      last(),
      switchMap(_ => this.loadContacts()),
      finalize(() => this.activeSubscriptions--)
    ))
  }

  savePhoto(value: V3FileItem) {
    this.activeSubscriptions++
    this.saveQueue.enqueue(this.peopleV3Rest.updatePhoto(this.personId, value).pipe(
      switchMap(_ => this.loadPhoto()),
      finalize(() => this.activeSubscriptions--)
    ))
  }
}
